import { MarketingNotificationsState } from 'src/domains/players/state/MarketingNotificationsState';
import { UsersState } from 'src/domains/players/state/UsersState';
import { CustomKeyboardState } from 'src/domains/players/state/CustomKeyboardState';
import { AccountState } from 'src/domains/players/state/accountState/AccountState';
import { ConfirmationBoxState } from 'src/domains/players/state/ConfirmationBoxState';
import { MessageBoxState } from 'src/domains/players/state/MessageBoxState';
import { SignupPopupState } from 'src/domains/players/state/SignupPopupState';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { StarLoginState } from 'src/domains/players/state/starLoginState/StarLoginState';
import { SignUpState } from 'src/domains/players/webview/components/SignUp';
import { SpecialEvents } from 'src/domains/players/state/specialEventsState/SpecialEvents';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { action, autorun } from 'mobx';
import { CmsDrawerPagesState } from 'src/domains/players/state/CmsDrawerPagesState';
import { createAccountPrimary } from 'src_server/trpc/types';
import { ExternalApi } from 'src/domains/layouts/state/externalApi/ExternalApi';
import { Common } from 'src/domains/common/Common';
import { BasicDataModel } from './BasicDataModel';
import { ModalState } from 'src/domains/layouts/webview/components/modals/Modal.state';
import { AccountDrawerNew } from 'src/domains/layouts/state/AccountDrawerNew.state';
import { BlockingNotificationsState } from './BlockingNotification.state';
import { IdentityState } from './IdMission/IdentityState';
import { NonBlockingNotificationsState } from './NonBlockingNotification.state';
import { AppLayoutsState } from 'src/domains/layouts/state/AppLayouts.state';
import { EnvironmentState } from 'src/domains/layouts/state/environmentState/EnvironmentState';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';

const AccountStatusEnum = createAccountPrimary.AccountStatusEnum;

export class AppPlayersState {
    public readonly marketingNotificationsState: MarketingNotificationsState;
    public readonly customKeyboard: CustomKeyboardState;
    public readonly confirmationBox: ConfirmationBoxState;
    public readonly messageBox: MessageBoxState;
    public readonly signupPopupState: SignupPopupState;
    public readonly loginState: StarLoginState;
    public readonly signUpState: SignUpState;
    public readonly specialEvents: SpecialEvents;
    public readonly cmsDrawerPagesState: CmsDrawerPagesState;
    public readonly accountDrawerNew: AccountDrawerNew;
    public readonly modalState: ModalState;
    public readonly blockingNotificationsState: BlockingNotificationsState;
    public readonly nonBlockingNotificationsState: NonBlockingNotificationsState;
    public readonly identityState: IdentityState | null;

    public static get = AutoWeakMap.create((common: Common) => new AppPlayersState(common));

    private constructor(private readonly common: Common) {
        this.marketingNotificationsState = MarketingNotificationsState.get(this.common);

        this.modalState = ModalState.get(this.common);

        this.customKeyboard = CustomKeyboardState.get(this.common);
        this.confirmationBox = ConfirmationBoxState.get(this.common);
        this.messageBox = MessageBoxState.get(this.common);
        this.signupPopupState = SignupPopupState.get(this.common);

        this.loginState = StarLoginState.get(common);

        this.accountDrawerNew = new AccountDrawerNew(
            AppLayoutsState.get(common).bannersBoxState,
            this.modalState,
            this.common
        );

        this.signUpState = SignUpState.get(common);

        this.specialEvents = SpecialEvents.get(common);
        this.cmsDrawerPagesState = CmsDrawerPagesState.get(common);
        this.blockingNotificationsState = BlockingNotificationsState.get(this.common);
        this.nonBlockingNotificationsState = NonBlockingNotificationsState.get(this.common);

        this.identityState = IdentityState.get(this.common);

        StarRouter.get(common).onChangeAccountView(async (prevAccountView, nextAccountView) => {
            const account = nextAccountView?.account;
            if (account === 'top-up' || account === 'limits') {
                this.refreshDepositLimitsResources();
            }

            if (prevAccountView?.account === 'signup' && nextAccountView?.account !== 'signup') {
                this.signUpState.rebuildState();
            }
        });

        if (EnvironmentState.get(common).isBrowser) {
            this.runOnStart();
        }
        this.detectBasicData(ConfigComponents.get(common).config.detectLoginInMobileApp);
    }

    private runOnStart = (): void => {
        autorun(async () => {
            const accountState = AccountState.get(this.common);
            const accountStatus = accountState.account?.basicDataReady?.status;

            if (accountStatus === AccountStatusEnum.SUSPENDED) {
                await accountState.handleLogout();
            }
        });

        let prevUser: number | null = null;
        autorun(() => {
            const userId = this.common.session.userId;

            if (prevUser !== null && userId === null) {
                this.messageBox.runActionNotLogged();
            }

            if (userId !== null && userId !== prevUser) {
                this.refreshDepositLimitsResources();
            }

            prevUser = userId;
        });
    };

    private refreshDepositLimitsResources = (): void => {
        const usersState = UsersState.get(this.common);
        usersState.depositLimitsData.refresh();
        usersState.rollingNetDepositLimitData.refresh();
    };

    private detectBasicData = (detectLoginInMobileApp: boolean): void => {
        if (detectLoginInMobileApp === false) {
            return;
        }

        autorun((dispose) => {
            const basicDataComp = BasicDataModel.get(this.common).basicDataReady;
            const id = basicDataComp?.id ?? null;
            const pushNotificationsEnabled = basicDataComp?.pushNotificationsEnabled;
            const isDataValid = id !== null;
            if (isDataValid) {
                this.sendInfoAboutFirstLoginToMobileApp(id, pushNotificationsEnabled);
                dispose.dispose();
            }
        });
    };

    @action private sendInfoAboutFirstLoginToMobileApp = (
        id: number,
        pushNotificationsEnabled: boolean | null | undefined
    ): void => {
        if (pushNotificationsEnabled === null || pushNotificationsEnabled === undefined) {
            return;
        }
        ExternalApi.get(this.common).sendFirstLoginMsg(id, pushNotificationsEnabled);
    };
}
